import React, { useState } from "react";
import { ApolloError } from "@apollo/client";
import { Banner, Button, Flex, Text, toast } from "@jsq/ds/core";
import { addAction } from "@jsq/observability";
import { ErrorPage, ErrorType } from "../../../../common/components/ErrorPage";
import { useSubmitChangeRequestPaymentPrefMutation } from "../../../../graphql/supergraph.index";
import { positionPaymentPrefVar } from "../../Containers/PaymentPrefForPositionContainer/apollo-vars";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";
import { PaymentPrefConfirmationDisplayCard } from "../PaymentPrefConfirmationDisplayCard";
import { type ChildComponentProps } from "../types";
import { CustomConfirmationError, ERROR_UPDATING_PREF_MISSING_INFORMATION } from "./errors";
import {
  useDetermineAssociatePaymentPrefMutation,
  useDetermineCreatePaymentPrefMutation,
} from "./hooks";
import {
  convertPaymentPrefDetailToInputType,
  getAssociationMutationVariables,
  submitChangeRequest,
} from "./utils";

export const PaymentPrefConfirmation: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName = "Back",
}) => {
  const {
    currentPosition,
    associatePaymentPref,
    paymentPrefDetail,
    ListOfPositionsToUpdateComponent,
    sourcePaymentPrefId,
    paymentPrefId,
    selectedPaymentMethod,
    selectedPaymentPref,
    setPaymentPrefId,
    isExistingPaymentPref,
    accountName,
    arena,
  } = useConsolidatedPaymentPrefContext();

  const [confirming, setConfirming] = useState(false);
  const { createNewPaymentPrefMutation, resultKey } =
    useDetermineCreatePaymentPrefMutation(selectedPaymentMethod);

  const { associatePaymentPrefMutation, associatePaymentPrefKey } =
    useDetermineAssociatePaymentPrefMutation(
      associatePaymentPref ?? AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsInAccount,
    );

  const [createChangeRequest] = useSubmitChangeRequestPaymentPrefMutation();

  // If there is no associating payment pref enum specified, there is nothing for us to call since we don't know
  // how to associate items to a payment pref. Return error for now.
  if (!associatePaymentPref || (!selectedPaymentPref && !paymentPrefDetail)) {
    return <ErrorPage error={ErrorType.InternalError} />;
  }

  const onContinueHandler = async () => {
    setConfirming(true);

    let localPaymentPrefId = paymentPrefId;

    // Create a new payment pref if user did not select a previous payment pref
    if (!isExistingPaymentPref && paymentPrefDetail) {
      const createPaymentPrefInput = convertPaymentPrefDetailToInputType(
        paymentPrefDetail,
        arena?.id,
      );

      // If the input type is empty, paymentPrefDetail couldn't be transformed.
      if (Object.keys(createPaymentPrefInput).length === 0) {
        toast.error("Could not create payment pref. Please try again later");
        setConfirming(false);
        return;
      }

      try {
        // This is temporary code being implemented here to investigate that validation is working correctly.
        // https://junipersquare.atlassian.net/browse/FF-3509
        if (paymentPrefDetail.__typename === "WirePaymentPrefObjectType") {
          addAction("create_wire_payment_pref");
        }

        const { data } = await createNewPaymentPrefMutation({
          variables: {
            // @ts-expect-error type will match based on input from context
            input: createPaymentPrefInput,
          },
        });

        // @ts-expect-error We are accessing the results with a dynamic key depending on which
        // create payment pref mutation we call.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const newPaymentPrefId: string | undefined = data?.[resultKey].paymentPref.id;

        if (!newPaymentPrefId) {
          toast.error("Error updating payment pref instructions. Please try again later");
          setConfirming(false);
          return;
        }

        setPaymentPrefId(newPaymentPrefId);
        localPaymentPrefId = newPaymentPrefId;
      } catch (e) {
        toast.error("Error creating new payment instructions. Please try again later");
        setConfirming(false);
        return;
      }
    }

    if (arena?.features?.hasPositionPaymentPrefStaffApprovals) {
      const arenaId = arena.id;
      const targetPaymentPrefId = localPaymentPrefId;
      const accountId = currentPosition?.accountId;
      if (!arenaId || !targetPaymentPrefId || !accountId) {
        setConfirming(false);
        toast.error(ERROR_UPDATING_PREF_MISSING_INFORMATION);
        return;
      }
      try {
        await submitChangeRequest(
          createChangeRequest,
          associatePaymentPref,
          arenaId,
          targetPaymentPrefId,
          accountId,
          sourcePaymentPrefId,
          currentPosition?.id,
          false,
          accountName,
        );
      } catch (error) {
        // TS requires type-checking for errors to expect message as a part of object returned in error
        if (error instanceof CustomConfirmationError || error instanceof ApolloError) {
          toast.error(error.message);
        }
        return;
      } finally {
        setConfirming(false);
      }
      onContinueClick();
      return;
    }

    const associationMutationVariables = getAssociationMutationVariables(
      associatePaymentPref,
      currentPosition?.accountId,
      sourcePaymentPrefId,
      localPaymentPrefId,
      currentPosition?.id,
      localPaymentPrefId,
    );

    if (!associationMutationVariables?.objectInput) {
      toast.error(ERROR_UPDATING_PREF_MISSING_INFORMATION);
      setConfirming(false);
      return;
    }

    try {
      const associatePaymentPrefResult = await associatePaymentPrefMutation({
        // @ts-expect-error All variables will be set at this point as long as previous context values are present
        variables: associationMutationVariables,
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const associatePaymentPrefData =
        // @ts-expect-error We are accessing the results with a dynamic key depending on which
        // create payment pref mutation we call.
        associatePaymentPrefResult?.data?.[associatePaymentPrefKey];

      // All mutations return MutationErrorResponse if the mutation failed.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (associatePaymentPrefData.__typename === "MutationErrorResponse") {
        toast.error(ERROR_UPDATING_PREF_MISSING_INFORMATION);
        setConfirming(false);
        return;
      }

      positionPaymentPrefVar(localPaymentPrefId);
    } catch (e) {
      toast.error(ERROR_UPDATING_PREF_MISSING_INFORMATION);
      setConfirming(false);
      return;
    }

    setConfirming(false);
    onContinueClick();
  };

  return (
    <>
      <Text className="stacked-lg" variant="h2">
        Confirm updated payment instruction
      </Text>
      {!!ListOfPositionsToUpdateComponent && (
        <div className="stacked-md">
          <ListOfPositionsToUpdateComponent />
        </div>
      )}
      <div className="stacked-lg">
        <PaymentPrefConfirmationDisplayCard />
      </div>
      {arena?.features?.hasPositionPaymentPrefStaffApprovals && (
        <div className="stacked-lg">
          <Banner
            variant="primary"
            title=""
            description="Another staff user must approve these payment instructions before they can be used for transactions"
          />
        </div>
      )}
      <Flex flexDirection="row-reverse" gap={8}>
        <Button isLoading={confirming} onClick={() => void onContinueHandler()}>
          {arena?.features?.hasPositionPaymentPrefStaffApprovals
            ? "Submit for review"
            : "Confirm"}
        </Button>
        <Button variant="neutral" onClick={onBackClick}>
          {onBackButtonName}
        </Button>
      </Flex>
    </>
  );
};
